import type { HTMLAttributes, ReactNode } from 'react';
import { DT } from '../../theme/designTokens';

type CardVariant = 'default' | 'elevated' | 'bordered' | 'gold';

interface Props extends HTMLAttributes<HTMLDivElement> {
  variant?:  CardVariant;
  padding?:  number | string;
  children:  ReactNode;
  clickable?: boolean;
  // Design Master source: article cards use absolute inner div with radial-gradient + group-hover:opacity-100
  // Enable this prop to add the Design Master card glow child element.
  hoverGlow?: boolean;
}

function cardBackground(v: CardVariant): string {
  switch (v) {
    case 'elevated': return DT.color.cardBgElevated;
    case 'bordered': return DT.color.cardBg;
    case 'gold':     return DT.color.cardBg;
    default:         return DT.color.cardBg;
  }
}

function cardBorder(v: CardVariant): string {
  switch (v) {
    case 'bordered': return `1px solid ${DT.color.borderStrong}`;
    case 'gold':     return `1px solid ${DT.color.borderGold}`;
    default:         return `1px solid ${DT.color.border}`;
  }
}

export function Card({
  variant   = 'default',
  padding   = DT.layout.cardGap,
  clickable = false,
  hoverGlow = false,
  children,
  style,
  ...rest
}: Props) {
  return (
    <div
      style={{
        position:     hoverGlow ? 'relative' : undefined,
        overflow:     hoverGlow ? 'hidden'   : undefined,
        background:   cardBackground(variant),
        border:       cardBorder(variant),
        borderRadius: DT.radius.lg,
        padding:      typeof padding === 'number' ? `${padding}px` : padding,
        cursor:       clickable ? 'pointer' : undefined,
        // Design Master source: Tailwind .transition default = 0.15s cubic-bezier(0.4,0,0.2,1)
        transition:   clickable || hoverGlow ? 'border-color .15s cubic-bezier(0.4,0,0.2,1)' : undefined,
        ...style,
      }}
      onMouseEnter={clickable || hoverGlow ? e => {
        const el = e.currentTarget as HTMLDivElement;
        // Design Master source: article hover:border-gold/60 = rgba(201,168,76,0.60)
        el.style.borderColor = DT.hover.cardBorder;
        // Reveal inner glow child (if hoverGlow enabled)
        if (hoverGlow) {
          const glow = el.querySelector<HTMLDivElement>('[data-card-glow]');
          if (glow) glow.style.opacity = '1';
        }
      } : undefined}
      onMouseLeave={clickable || hoverGlow ? e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.borderColor = '';
        if (hoverGlow) {
          const glow = el.querySelector<HTMLDivElement>('[data-card-glow]');
          if (glow) glow.style.opacity = '0';
        }
      } : undefined}
      {...rest}
    >
      {hoverGlow && (
        // Design Master source: article > div[aria-hidden] with radial-gradient + opacity-0 + group-hover:opacity-100 + blur-3xl
        // Absolute inner glow — invisible at rest, fades to visible on hover.
        <div
          aria-hidden="true"
          data-card-glow
          style={{
            position:   'absolute',
            inset:      0,
            background: 'radial-gradient(600px circle at 100% 0%, rgba(201,168,76,0.12), transparent 60%)',
            opacity:    0,
            transition: 'opacity .15s cubic-bezier(0.4,0,0.2,1)',
            pointerEvents: 'none',
          }}
        />
      )}
      {children}
    </div>
  );
}
